import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _typeof from "@babel/runtime/helpers/typeof";
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
/**
 * Permissive reader for tests/<id>/auteur/execution.json — the v4 authoring
 * payload kane-cli embeds in the pack. Worker-portable (no React, no data/ui
 * imports). Same posture as run.yaml parsing: guard every hop, ignore unknown
 * keys, and return null rather than throw — a broken execution.json degrades
 * the steps tree to the result.yaml flat list, never the whole detail view.
 *
 * Shape (observed, contract still settling backend-side):
 *   { instructions: [ { content_type: 'generative_block', objective, status,
 *       children: [ { content_type: 'instruction', instruction, command,
 *         status: 'success'|'failed', reasoning, error_message,
 *         operations: [{ operation_id, error_message, x_coord, y_coord }] } ]
 *   } ], start_timestamp, end_timestamp, status }
 *
 * Nested containers (branches[].children, module_content) are flattened into
 * their block's leaf list. The one exception is a top-level `module_block`:
 * it keeps its identity (blue module card) and its direct children as
 * sub-groups, so a generative block inside a module still renders as one.
 */

/**
 * EVIDENCE-VIEWER PARITY port of kaneai's sanitizeObjectiveText
 * (apps/kaneai-test-management-client/src/components/v4/utils.ts):
 * strips the backend's internal "@v16:" breakdown markers from objective
 * text — unwraps each @v16:"..." segment (unescaping \" and \\) and
 * drops bare @v16: tokens. Pure display concern; packs keep the raw text.
 */
export function sanitizeObjectiveText(text) {
  if (!text) return text !== null && text !== void 0 ? text : null;
  if (!text.includes('@v16:')) return text;
  return text.replace(/@v16:\s*"((?:[^"\\]|\\.)*)"/g, function (_m, inner) {
    return inner.replace(/\\(["\\])/g, '$1');
  }).replace(/@v16:\s*/g, '');
}
function asRecord(v) {
  return v !== null && _typeof(v) === 'object' && !Array.isArray(v) ? v : null;
}
var asString = function asString(v) {
  return typeof v === 'string' && v !== '' ? v : null;
};
var asFiniteNumber = function asFiniteNumber(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

/** One if/else branch, structured for the rich card (kaneai
 * ConditionalBlockV4 port). */

/** Loose stringification for operand values (numbers author as numbers). */
var asOperand = function asOperand(v) {
  return typeof v === 'string' && v !== '' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : null;
};
function toAssertion(op) {
  var _asOperand, _asOperand2;
  if (!op) return null;
  var tree = asRecord(op.expression_tree);
  var checks = tree ? Array.isArray(tree.sub_results) && tree.sub_results.length > 0 ? tree.sub_results : Array.isArray(tree.sub_checks) ? tree.sub_checks : [] : [];
  var sub = asRecord(checks[0]);
  var passed = typeof op.assertion_status === 'boolean' ? op.assertion_status : op.assertion_status === 'pass' ? true : sub && typeof sub.passed === 'boolean' ? sub.passed : null;
  if (!sub && passed === null) return null;
  return {
    passed: passed,
    extracted: sub ? (_asOperand = asOperand(sub.extracted_value)) !== null && _asOperand !== void 0 ? _asOperand : asOperand(sub.store_key) : null,
    expected: sub ? (_asOperand2 = asOperand(sub.expected_value)) !== null && _asOperand2 !== void 0 ? _asOperand2 : asOperand(sub.expected) : null,
    operator: sub ? asString(sub.operator) : null
  };
}

/** api leaves: content_type 'api' / command 'API'; the sealed api_data
 * (node-level, or operations[0]) carries the request + response. */
/** Rare producer shape: no script_content — the script rides inside the
 * instruction title as `@executor <cmd> \`<script>\``. Parsing is the
 * FALLBACK only (authored keys always win); no fence → null. */
var FENCED_EXECUTOR = /^\s*@executor\s+(\S+)\s*`([\s\S]+)`\s*$/;

/** EXECUTE_CUSTOM_SCRIPT leaves — gate on the authored command, never on
 * field presence (script_content-shaped keys exist on every operation). */
export function toCustomScript(node, op) {
  var _ref, _asString, _ref4, _asString2, _ref5, _ref6;
  var cmd = ((_ref = (_asString = asString(node.command)) !== null && _asString !== void 0 ? _asString : asString(op === null || op === void 0 ? void 0 : op.command)) !== null && _ref !== void 0 ? _ref : '').toUpperCase();
  if (cmd !== 'EXECUTE_CUSTOM_SCRIPT') return null;
  var scriptContent = op ? asString(op.script_content) : undefined;
  if (scriptContent) {
    var _ref2, _ref3;
    return {
      command: (_ref2 = op ? asString(op.exec_command) : undefined) !== null && _ref2 !== void 0 ? _ref2 : '',
      scriptContent: scriptContent,
      timeoutSec: op && typeof op.timeout_sec === 'number' && Number.isFinite(op.timeout_sec) ? op.timeout_sec : null,
      execId: (_ref3 = op ? asString(op.exec_id) : undefined) !== null && _ref3 !== void 0 ? _ref3 : null
    };
  }
  var instructionText = (_ref4 = (_asString2 = asString(node.instruction)) !== null && _asString2 !== void 0 ? _asString2 : asString(op === null || op === void 0 ? void 0 : op.instruction)) !== null && _ref4 !== void 0 ? _ref4 : '';
  var fenced = FENCED_EXECUTOR.exec(instructionText);
  if (!fenced) return null;
  return {
    // exec_command still wins over the parsed token when authored.
    command: (_ref5 = op ? asString(op.exec_command) : undefined) !== null && _ref5 !== void 0 ? _ref5 : fenced[1],
    scriptContent: fenced[2],
    timeoutSec: op && typeof op.timeout_sec === 'number' && Number.isFinite(op.timeout_sec) ? op.timeout_sec : null,
    execId: (_ref6 = op ? asString(op.exec_id) : undefined) !== null && _ref6 !== void 0 ? _ref6 : null
  };
}

/** Stringify an execution result value — "0"/"false" are real, only
 * null/undefined collapse. */
function asDisplayValue(v) {
  if (v === null || v === undefined) return null;
  // JSON form throughout — strings render quoted ("tarun-singh"), matching
  // kaneai's set-variable pills (VariableViewerFooter JSON.stringify).
  try {
    var _JSON$stringify;
    return (_JSON$stringify = JSON.stringify(v)) !== null && _JSON$stringify !== void 0 ? _JSON$stringify : String(v);
  } catch (_unused) {
    return String(v);
  }
}

/** First authored user variable ({name, value}) — kaneai's
 * VariableViewerFooter source (op first, node fallback). */
function firstUserVariable(node, op) {
  // Producers author this either as a real array or as a JSON string of
  // one — normalize both (string form seen in kane-cli packs).
  var asList = function asList(raw) {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim().startsWith('[')) {
      try {
        var parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_unused2) {
        return [];
      }
    }
    return [];
  };
  var opList = op ? asList(op.user_variables) : [];
  var list = opList.length > 0 ? opList : asList(node.user_variables);
  var v = asRecord(list[0]);
  var name = v ? asString(v.name) : null;
  if (!name) return null;
  return {
    name: name,
    value: asDisplayValue(v ? v.value : null)
  };
}

/** content_type 'script' → JS-snippet rich card (TE-27602). */
export function toJsScript(node, op) {
  var _asString3, _asString4, _userVar$value, _userVar$name;
  if (asString(node.content_type) !== 'script') return null;
  var snippet = (_asString3 = asString(node.js_snippet)) !== null && _asString3 !== void 0 ? _asString3 : op ? (_asString4 = asString(op.js_snippet)) !== null && _asString4 !== void 0 ? _asString4 : asString(op.script_content) : null;
  if (!snippet || !snippet.trim()) return null;
  var result = op ? asRecord(op.execution_result) : null;
  var codeError = op ? asRecord(op.code_error) : null;
  // kaneai footer source: user_variables first, op fields as fallback.
  var userVar = firstUserVariable(node, op);
  return {
    snippet: snippet.replace(/^\n+/, ''),
    resultValue: (_userVar$value = userVar === null || userVar === void 0 ? void 0 : userVar.value) !== null && _userVar$value !== void 0 ? _userVar$value : asDisplayValue(result ? result.value : null),
    outputVariable: (_userVar$name = userVar === null || userVar === void 0 ? void 0 : userVar.name) !== null && _userVar$name !== void 0 ? _userVar$name : op ? asString(op.output_variable_name) : null,
    errorLine: codeError ? asFiniteNumber(codeError.line) : null,
    errorMessage: codeError ? asString(codeError.error) : null
  };
}

/** Best-effort base64 → text; returns the input when it isn't base64 or
 * decodes to control characters (some producers send the query plain). */
function decodeMaybeBase64(raw) {
  try {
    var decoded = atob(raw);
    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x08\x0e-\x1f]/.test(decoded)) return raw;
    return decoded;
  } catch (_unused3) {
    return raw;
  }
}

/** content_type 'db_query' → database rich card (TE-27602). */
export function toDbQuery(node, op) {
  var _ref7, _asString5, _asString6, _asRecord, _userVar$value2, _userVar$name2;
  var ct = asString(node.content_type);
  var cmd = ((_ref7 = (_asString5 = asString(node.command)) !== null && _asString5 !== void 0 ? _asString5 : asString(op === null || op === void 0 ? void 0 : op.command)) !== null && _ref7 !== void 0 ? _ref7 : '').toUpperCase();
  if (ct !== 'db_query' && cmd !== 'DB_QUERY') return null;
  // Authored decoded key wins over decoding ourselves.
  var decoded = op ? asString(op.db_query_decoded) : null;
  var raw = (_asString6 = asString(node.db_query)) !== null && _asString6 !== void 0 ? _asString6 : op ? asString(op.db_query) : null;
  var query = decoded !== null && decoded !== void 0 ? decoded : raw ? decodeMaybeBase64(raw) : null;
  var details = (_asRecord = asRecord(node.db_details)) !== null && _asRecord !== void 0 ? _asRecord : op ? asRecord(op.db_details) : null;
  if (!query && !details) return null;
  // kaneai footer source: user_variables first, query_variable_name as
  // fallback (some producers leave it "" and author user_variables only).
  var userVar = firstUserVariable(node, op);
  return {
    query: query,
    dbName: details ? asString(details.db_name) : null,
    output: (_userVar$value2 = userVar === null || userVar === void 0 ? void 0 : userVar.value) !== null && _userVar$value2 !== void 0 ? _userVar$value2 : asDisplayValue(details ? details.output : null),
    outputVariable: (_userVar$name2 = userVar === null || userVar === void 0 ? void 0 : userVar.name) !== null && _userVar$name2 !== void 0 ? _userVar$name2 : op ? asString(op.query_variable_name) : null
  };
}

/** content_type 'screenshot_command' → SmartUI rich card (TE-27602). */
export function toVisual(node, op) {
  var _ref8, _asString7, _ref9, _ref0;
  var ct = asString(node.content_type);
  var cmd = ((_ref8 = (_asString7 = asString(node.command)) !== null && _asString7 !== void 0 ? _asString7 : asString(op === null || op === void 0 ? void 0 : op.command)) !== null && _ref8 !== void 0 ? _ref8 : '').toUpperCase();
  if (ct !== 'screenshot_command' && cmd !== 'SMARTUI_SCREENSHOT') return null;
  // Authored op.screenshot_command first; the raw instruction/title JSON is
  // the fallback (that raw JSON is exactly what the plain card showed).
  var rawConfig = (_ref9 = (_ref0 = op ? asString(op.screenshot_command) : null) !== null && _ref0 !== void 0 ? _ref0 : asString(node.instruction)) !== null && _ref9 !== void 0 ? _ref9 : asString(node.title);
  var config = {};
  if (rawConfig) {
    try {
      var _asRecord2;
      config = (_asRecord2 = asRecord(JSON.parse(rawConfig))) !== null && _asRecord2 !== void 0 ? _asRecord2 : {};
    } catch (_unused4) {
      config = {};
    }
  }
  var name = asString(config.name);
  var smartui = op ? asRecord(op.smartui_results) : null;
  var screenshots = smartui ? asRecord(smartui.screenshots) : null;
  var shots = screenshots && name && Array.isArray(screenshots["".concat(name, ".png")]) ? screenshots["".concat(name, ".png")] : [];
  var shot = asRecord(shots[0]);
  var build = smartui ? asRecord(smartui.build) : null;
  return {
    name: name,
    captureType: asString(config.type),
    threshold: asString(config.mismatch_threshold),
    smartIgnore: asString(config.smartIgnore) === 'true',
    resultStatus: shot ? asString(shot.status) : null,
    mismatchPct: shot ? asFiniteNumber(shot.mismatch_percentage) : null,
    strategy: build ? asString(build.comparisonStrategy) : null,
    baseline: build ? build.baseline === true : false
  };
}
function toApi(node, op) {
  var _asString8, _asRecord3, _ref1;
  var ct = asString(node.content_type);
  var cmd = ((_asString8 = asString(node.command)) !== null && _asString8 !== void 0 ? _asString8 : '').toLowerCase();
  if (ct !== 'api' && cmd !== 'api') return null;
  var data = (_asRecord3 = asRecord(node.api_data)) !== null && _asRecord3 !== void 0 ? _asRecord3 : op ? asRecord(op.api_data) : null;
  var request = data ? asRecord(data.request) : null;
  var response = data ? asRecord(data.response) : null;
  return {
    method: (_ref1 = request ? asString(request.method) : null) !== null && _ref1 !== void 0 ? _ref1 : 'GET',
    url: request ? asString(request.url) : null,
    responseStatus: response ? asFiniteNumber(response.status) : null,
    responseTimeMs: response ? asFiniteNumber(response.time) : null,
    request: request,
    response: response
  };
}
function toLeaf(node) {
  var _asString9;
  var ops = Array.isArray(node.operations) ? node.operations : [];
  var op = asRecord(ops[0]);
  var x = op ? asFiniteNumber(op.x_coord) : null;
  var y = op ? asFiniteNumber(op.y_coord) : null;
  var command = asString(node.command);
  return {
    instructionId: asString(node.instruction_id),
    instruction: asString(node.instruction),
    command: command,
    status: asString(node.status),
    reasoning: asString(node.reasoning),
    intent: op ? asString(op.intent) : null,
    operationId: op ? asString(op.operation_id) : null,
    errorMessage: (_asString9 = asString(node.error_message)) !== null && _asString9 !== void 0 ? _asString9 : op ? asString(op.error_message) : null,
    cursor: x !== null && y !== null ? {
      x: x,
      y: y
    } : null,
    assertion: command && command.toLowerCase() === 'assertion' ? toAssertion(op) : null,
    api: toApi(node, op),
    customScript: toCustomScript(node, op),
    jsScript: toJsScript(node, op),
    dbQuery: toDbQuery(node, op),
    visual: toVisual(node, op)
  };
}
function toIfBranch(br, index) {
  var _asOperand3, _asOperand4;
  var condition = asString(br.condition);
  var checks = Array.isArray(br.sub_checks) ? br.sub_checks : [];
  var sub = asRecord(checks[0]);
  var leaves = [];
  if (Array.isArray(br.children)) {
    var _iterator = _createForOfIteratorHelper(br.children),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var child = _step.value;
        var c = asRecord(child);
        if (c) collectLeaves(c, leaves);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }
  return {
    label: index === 0 ? 'If' : condition !== null ? 'Else If' : 'Else',
    condition: condition,
    passed: typeof br.passed === 'boolean' ? br.passed : null,
    subCheck: sub ? {
      left: (_asOperand3 = asOperand(sub.extracted_value)) !== null && _asOperand3 !== void 0 ? _asOperand3 : asOperand(sub.store_key),
      operator: asString(sub.operator),
      right: (_asOperand4 = asOperand(sub.expected)) !== null && _asOperand4 !== void 0 ? _asOperand4 : asOperand(sub.expected_value)
    } : null,
    leaves: leaves
  };
}

/** Depth-first leaf collection across every container shape the format has
 * grown so far. conditional_block / until_block become CONTAINER leaves —
 * one leaf carrying its nested leaves as a structured payload (the rich
 * if/else + until cards) instead of silently flattening/dropping them. */
function collectLeaves(node, out) {
  var ct = asString(node.content_type);
  if (ct === 'conditional_block') {
    var branches = Array.isArray(node.branches) ? node.branches : [];
    var leaf = toLeaf(node);
    leaf.ifElse = {
      resolvedBranchIndex: typeof node.resolved_branch_index === 'number' ? node.resolved_branch_index : null,
      branches: branches.map(function (b, i) {
        var br = asRecord(b);
        return br ? toIfBranch(br, i) : null;
      }).filter(function (b) {
        return b !== null;
      })
    };
    out.push(leaf);
    return;
  }
  if (ct === 'until_block') {
    var nested = [];
    if (Array.isArray(node.children)) {
      var _iterator2 = _createForOfIteratorHelper(node.children),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var child = _step2.value;
          var c = asRecord(child);
          if (c) collectLeaves(c, nested);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
    }
    var _leaf = toLeaf(node);
    _leaf.until = {
      label: asString(node.block_label),
      condition: asString(node.condition),
      conditionMet: typeof node.condition_met === 'boolean' ? node.condition_met : null,
      currentCount: asFiniteNumber(node.current_count),
      maxRetries: asFiniteNumber(node.max_retries),
      leaves: nested
    };
    out.push(_leaf);
    return;
  }
  if (ct === 'instruction' || asString(node.instruction)) {
    out.push(toLeaf(node));
    return;
  }
  var containers = [];
  if (Array.isArray(node.children)) containers.push.apply(containers, _toConsumableArray(node.children));
  if (Array.isArray(node.branches)) {
    var _iterator3 = _createForOfIteratorHelper(node.branches),
      _step3;
    try {
      for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
        var b = _step3.value;
        var br = asRecord(b);
        if (br && Array.isArray(br.children)) containers.push.apply(containers, _toConsumableArray(br.children));
      }
    } catch (err) {
      _iterator3.e(err);
    } finally {
      _iterator3.f();
    }
  }
  if (Array.isArray(node.module_content)) containers.push.apply(containers, _toConsumableArray(node.module_content));
  for (var _i = 0, _containers = containers; _i < _containers.length; _i++) {
    var _child = _containers[_i];
    var _c = asRecord(_child);
    if (_c) collectLeaves(_c, out);
  }
}

/** module_version authors as a number (1.1) — keep it as display text. */
var asVersion = function asVersion(v) {
  return typeof v === 'number' && Number.isFinite(v) ? String(v) : asString(v);
};

/** kaneai blockId order: module_instance_id, then the ids a generative
 * block / bare instruction carries. */
var blockKey = function blockKey(node, fallback) {
  var _ref10, _ref11, _asString0;
  return (_ref10 = (_ref11 = (_asString0 = asString(node.module_instance_id)) !== null && _asString0 !== void 0 ? _asString0 : asString(node.generative_id)) !== null && _ref11 !== void 0 ? _ref11 : asString(node.instruction_id)) !== null && _ref10 !== void 0 ? _ref10 : fallback;
};

/** A module's direct children: v4 `children`, v2 `module_content`. */
function moduleChildren(node) {
  var raw = Array.isArray(node.children) ? node.children : Array.isArray(node.module_content) ? node.module_content : [];
  return raw.map(function (c) {
    return asRecord(c);
  }).filter(function (c) {
    return c !== null;
  });
}

/**
 * One top-level instruction (or a module's direct child) → group. A
 * module_block keeps its children as sub-groups: generative blocks and
 * nested modules recurse, consecutive bare steps / containers share one
 * 'flat' group. Everything else flattens to leaves as before.
 */
function toBlock(node, fallbackKey) {
  var _asString1;
  var key = blockKey(node, fallbackKey);
  if (asString(node.content_type) === 'module_block') {
    var groups = [];
    var flat = [];
    var flatKey = '';
    var flushFlat = function flushFlat() {
      if (flat.length === 0) return;
      groups.push({
        key: flatKey,
        contentType: 'flat',
        objective: null,
        status: null,
        leaves: flat
      });
      flat = [];
    };
    moduleChildren(node).forEach(function (child, ci) {
      var ct = asString(child.content_type);
      if (ct === 'module_block' || ct === 'generative_block') {
        flushFlat();
        groups.push(toBlock(child, "".concat(key, "-").concat(ci)));
        return;
      }
      if (flat.length === 0) flatKey = blockKey(child, "".concat(key, "-").concat(ci));
      collectLeaves(child, flat);
    });
    flushFlat();
    return {
      key: key,
      contentType: 'module_block',
      objective: null,
      status: asString(node.status),
      leaves: groups.flatMap(function (g) {
        return g.leaves;
      }),
      module: {
        name: asString(node.module_name),
        version: asVersion(node.module_version),
        description: asString(node.module_description),
        moduleId: asString(node.module_id),
        instanceId: asString(node.module_instance_id)
      },
      blocks: groups
    };
  }
  var leaves = [];
  collectLeaves(node, leaves);
  var isLeafItself = leaves.length === 1 && leaves[0].instructionId === asString(node.instruction_id);
  return {
    key: key,
    // A bare top-level instruction becomes its own single-leaf group.
    contentType: isLeafItself ? 'flat' : (_asString1 = asString(node.content_type)) !== null && _asString1 !== void 0 ? _asString1 : 'generative_block',
    objective: isLeafItself ? null : sanitizeObjectiveText(asString(node.objective)),
    status: asString(node.status),
    leaves: leaves
  };
}

/**
 * Parse execution.json text. Null on anything that isn't a readable
 * instructions payload; blocks with zero recognizable leaves survive
 * (their objective still renders).
 */
export function parseExecution(text) {
  var doc;
  try {
    doc = asRecord(JSON.parse(text));
  } catch (_unused5) {
    return null;
  }
  if (!doc || !Array.isArray(doc.instructions)) return null;
  var blocks = [];
  doc.instructions.forEach(function (raw, i) {
    var node = asRecord(raw);
    if (node) blocks.push(toBlock(node, "block-".concat(i)));
  });
  return {
    status: asString(doc.status),
    startTimestamp: asString(doc.start_timestamp),
    endTimestamp: asString(doc.end_timestamp),
    blocks: blocks
  };
}